import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { MAIN_CONTENT_ID } from '@/components/accessibility/SkipToMain';
import AuthForm from '@/components/authForm/AuthForm';
import CheckoutSessionsPaymentForm from '@/components/purchase/CheckoutSessionsPaymentForm';
import LoggedInAs from '@/components/purchase/LoggedInAs';
import { useStripeElements } from '@/components/purchase/StripeElementsProvider';
import annualOfferMessages from '@/components/SimplifiedSignup/configs/AnnualOffer/messages';
import lifetimeOfferMessages from '@/components/SimplifiedSignup/configs/LifetimeOffer/messages';
import monthlyOfferMessages from '@/components/SimplifiedSignup/configs/MonthlyOffer/messages';
import type { PaymentScreenConfig } from '@/components/SimplifiedSignup/Payment/types';
import usePurchaseBodyArgs from '@/components/Sisu/hooks/usePurchaseBodyArgs';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import { usePurchaseParamsState, useUserState } from '@/hooks/store';
import genericMessages from '@/messages/messages';
import { isValidPlan, type PlanCadence } from '@/store/purchaseParams/types';
import { User } from '@/store/user/types';

import { UPSELL_LANDING_OFFER_FLOW_NAME } from './constants';
import { Heading, Wrapper } from './styles';
import { CheckoutFormProps } from './types';

// Quarterly keeps the form's cadence-aware default terms.
const paymentScreenConfigByPlan: Partial<Record<PlanCadence, Partial<PaymentScreenConfig>>> = {
	lifetime: {
		terms: lifetimeOfferMessages.terms,
		offerTerms: lifetimeOfferMessages.offerTerms,
	},
	yearly: {
		terms: annualOfferMessages.terms,
		offerTerms: annualOfferMessages.offerTerms,
	},
	monthly: {
		terms: monthlyOfferMessages.terms,
		offerTerms: monthlyOfferMessages.offerTerms,
	},
};

const CheckoutForm = ({
	email: _email,
	isCheckoutSessionsEnabled,
	onPurchaseSuccess: _onPurchaseSuccess,
}: CheckoutFormProps) => {
	const { authPrompt } = useAuthPrompt();
	const { plan } = usePurchaseParamsState();
	const user = useUserState();
	const { formatMessage } = useIntl();
	const [email, setEmail] = useState(_email ?? undefined);
	const [currentForm, setCurrentForm] = useState<'auth' | 'cc'>(email ? 'cc' : 'auth');
	const { isStripeNextEnabled } = useStripeElements();
	const { logEvent } = useAnalytics();
	const { freeTrial: isTrialPurchase } = usePurchaseBodyArgs({});

	// A visitor who arrives with only an email and no session can only buy through
	// subscription/email, which has no Checkout Sessions path. A completed email-only purchase
	// stores a user with a subscription but no id, so the id is what marks a real session.
	const isEmailOnlyPurchase = Boolean(email) && !user?.id;
	const flowPlan: PlanCadence = isValidPlan(plan) ? plan : 'yearly';

	const onLogout = () => {
		setEmail('');
		setCurrentForm('auth');
	};

	const onSuccess = () => {
		_onPurchaseSuccess?.();
	};

	const onAuthSuccess = (newUser: User) => {
		setEmail(newUser.email);
		setCurrentForm('cc');
	};

	useEffect(() => {
		if (currentForm === 'cc') {
			logEvent({
				eventName: 'Subscribe : Purchase : Form : Landed',
				eventProps: {
					is_stripe_next: isStripeNextEnabled,
				},
			});
		}
	}, [currentForm, isStripeNextEnabled, logEvent]);

	return (
		<Wrapper id={MAIN_CONTENT_ID}>
			{currentForm === 'auth' && (
				<>
					<Heading>
						{formatMessage(
							authPrompt === 'signup' ? genericMessages.signupTitleMedium : genericMessages.loginTitle,
						)}
					</Heading>
					<AuthForm onAuthSuccess={onAuthSuccess} initialValues={{ email }} />
				</>
			)}
			{currentForm === 'cc' && (
				<>
					<LoggedInAs onLogout={onLogout} email={user?.email ?? email} />
					<CheckoutSessionsPaymentForm
						flowName={UPSELL_LANDING_OFFER_FLOW_NAME}
						plan={flowPlan}
						pageName={flowPlan === 'lifetime' ? 'lifetime_offer' : 'annual_offer'}
						hasFreeTrial={isTrialPurchase}
						// The offer summaries describe an upfront charge, which a trial does not make.
						paymentScreenConfig={isTrialPurchase ? undefined : paymentScreenConfigByPlan[flowPlan]}
						isCheckoutSessionsEnabled={isCheckoutSessionsEnabled}
						endpoint={isEmailOnlyPurchase ? 'subscription/email' : 'subscription'}
						purchaseArgs={isEmailOnlyPurchase ? { email } : undefined}
						afterPurchaseSuccess={onSuccess}
					/>
				</>
			)}
		</Wrapper>
	);
};

export default CheckoutForm;
